import { Button, Flex, Group, Indicator, Menu, TextInput, type ComboboxItem } from '@mantine/core';
import i18n from '../../../../../base-dictionary';
import { inkLight, secondaryBase, secondaryLight, skyLightest } from '../../../../../styles/design-tokens';
import { Checkbox } from '../../../../../ui-library/checkbox/checkbox';
import { Icon } from '../../../../../ui-library/icon/icon';
import { SortDirection } from '../../initiative-performance.type';

type Props = {
  // Search
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  // Sort options
  sortOptions: ComboboxItem[];
  selectedSort: string;
  onSortChange: (sortId: string) => void;

  // Sort direction
  sortDirection: SortDirection;
  onSortDirectionChange: (direction: SortDirection) => void;

  // Filter options
  filterOptions: ComboboxItem[];
  selectedFilters: Record<string, boolean>;
  onFilterChange: (filterId: string, value: boolean) => void;
};

export const FilterListControls = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder = i18n.t('common.actions.search'),
  sortOptions,
  selectedSort,
  onSortChange,
  sortDirection = SortDirection.Asc,
  onSortDirectionChange,
  filterOptions,
  selectedFilters,
  onFilterChange,
}: Props) => {
  // Handler for clicking on a sort option
  const handleSortOptionClick = (optionValue: string) => {
    if (selectedSort === optionValue) {
      // If this option is already selected, toggle sort direction
      onSortDirectionChange(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      // Otherwise, select the new option
      onSortChange(optionValue);
      onSortDirectionChange(SortDirection.Asc);
    }
  };

  return (
    <Group gap={8} wrap="nowrap">
      <TextInput
        placeholder={searchPlaceholder}
        value={searchTerm}
        onChange={(e) => onSearchChange(e.currentTarget.value)}
        leftSection={<Icon name="search" size={14} color={inkLight} />}
        styles={{
          input: {
            borderRadius: 'var(--border-radius-xlarge)',
            height: 30,
            minHeight: 30,
          },
        }}
      />

      <Menu shadow="md" position="bottom-end" styles={{ dropdown: { width: 'fit-content', minWidth: 92 } }}>
        <Menu.Target>
          <Button
            variant="subtle"
            p={0}
            style={{
              backgroundColor: skyLightest,
              borderRadius: 8,
              width: 30,
              height: 30,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              transform: 'none',
              cursor: 'pointer',
            }}
          >
            <Icon name="format_line_spacing" size={20} color={secondaryLight} pointer />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          {sortOptions.map((option) => {
            const isSelected = selectedSort === option.value;

            return (
              <Menu.Item
                key={option.value}
                rightSection={isSelected ? <SortDirectionIcon direction={sortDirection} /> : null}
                onClick={() => handleSortOptionClick(option.value)}
                style={{
                  color: isSelected ? secondaryLight : 'inherit',
                  fontWeight: isSelected ? 500 : 400,
                }}
              >
                {option.label}
              </Menu.Item>
            );
          })}
        </Menu.Dropdown>
      </Menu>

      <Menu shadow="md" position="bottom-end" styles={{ dropdown: { width: 'fit-content', minWidth: 92 } }}>
        <Menu.Target>
          <Indicator
            disabled={Object.values(selectedFilters).every((value) => !value)}
            size={6}
            offset={2}
            color={secondaryBase}
          >
            <Button
              variant="subtle"
              p={0}
              style={{
                backgroundColor: skyLightest,
                borderRadius: 8,
                width: 30,
                height: 30,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Icon name="filter_list" size={20} color={secondaryLight} pointer />
            </Button>
          </Indicator>
        </Menu.Target>
        <Menu.Dropdown>
          {filterOptions.map((option) => (
            <Menu.Item
              key={option.value}
              closeMenuOnClick={false}
              onClick={(e) => {
                e.preventDefault();
                onFilterChange(option.value, !selectedFilters[option.value]);
              }}
            >
              <Checkbox
                checked={selectedFilters[option.value]}
                label={option.label}
                onClick={(e) => {
                  e.stopPropagation();
                  onFilterChange(option.value, !selectedFilters[option.value]);
                }}
              />
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

const SortDirectionIcon = ({ direction }: { direction: SortDirection }) => {
  return (
    <Flex style={{ cursor: 'pointer' }}>
      <Icon
        name={direction === SortDirection.Asc ? 'arrow_upward' : 'arrow_downward'}
        size={16}
        color={secondaryLight}
        pointer
      />
    </Flex>
  );
};
